import { format, parseISO, differenceInMinutes, isValid, formatDistanceToNow } from 'date-fns'
import { DateFormat } from 'utility/date-utility'

/** Strip HTML tags from API question text (e.g. rich-text FAQ). */
export const stripHtml = text => {
    if (!text) return ''
    const tmp = document.createElement('div')
    tmp.innerHTML = String(text)
    return (tmp.textContent || tmp.innerText || '').trim()
}

export const TICKET_STATUS = {
    1: { label: 'OPEN', pill: 'open', color: '#64748b' },
    2: { label: 'ACKNOWLEDGED', pill: 'ack', color: '#2563eb' },
    3: { label: 'IN PROGRESS', pill: 'progress', color: '#f59e0b' },
    4: { label: 'RESOLVED', pill: 'resolved', color: '#22c55e' },
    5: { label: 'CLOSED', pill: 'closed', color: '#71717a' },
    6: { label: 'ON HOLD', pill: 'hold', color: '#6d28d9' },
    7: { label: 'ESCALATED', pill: 'escalated', color: '#f97316' },
    8: { label: 'PENDING APPROVAL', pill: 'pending', color: '#7c3aed' },
    9: { label: 'CANCELLED', pill: 'cancelled', color: '#6b7280' },
    10: { label: 'CLOSED', pill: 'closed', color: '#71717a' },
    11: { label: 'CANCELLED', pill: 'cancelled', color: '#6b7280' },
}

export const TICKET_STATUS_PENDING_APPROVAL = 8
export const TICKET_STATUS_CANCELLED = 9

export const isTicketCancelledStatus = statusId => {
    const id = Number(statusId)
    return id === TICKET_STATUS_CANCELLED || id === 11
}

export const isTicketClosedStatus = statusId => {
    const id = Number(statusId)
    return id === 5 || id === 10
}

export const TICKET_STATUS_ESCALATED = 7

const toBoolFlag = value => value === true || value === 1 || value === '1'

export const TICKET_PRIORITY = {
    1: { label: 'LOW', pill: 'low', color: '#64748b' },
    2: { label: 'MEDIUM', pill: 'medium', color: '#b45309' },
    3: { label: 'HIGH', pill: 'high', color: '#b91c1c' },
    4: { label: 'CRITICAL', pill: 'critical', color: '#7f1d1d' },
}

export const formatUserLabel = user => {
    const full = user?.FullName ?? user?.fullName ?? user?.name
    const userName = user?.UserName ?? user?.userName
    const email = user?.Email ?? user?.email
    return (
        (full && String(full).trim()) ||
        (userName && String(userName).trim()) ||
        (email && String(email).trim()) ||
        (user?.UniqueId != null ? `User #${user.UniqueId}` : 'Unknown user')
    )
}

export const formatAssigneeDisplay = (fullName, email) => {
    const name = fullName?.trim()
    if (name) return name
    if (email?.trim()) return email.trim()
    return '—'
}

/** AssignedToTypeId: 1=User, 2=Team, 3=VendorCompany, 4=VendorContact */
export const getAssigneeTypeLabel = typeId => {
    switch (Number(typeId)) {
        case 1:
            return 'User'
        case 2:
            return 'Team'
        case 3:
            return 'Vendor company'
        case 4:
            return 'Vendor contact'
        default:
            return 'Assignee'
    }
}

/** Show name when API returns null for team/vendor assignees. */
export const formatTicketAssigneeLabel = (ticket, resolvedName = '') => {
    const fromApi = formatAssigneeDisplay(
        ticket?.assigneeFullName,
        ticket?.assigneeEmail,
    )
    if (fromApi && fromApi !== '—') return fromApi
    if (resolvedName?.trim()) return resolvedName.trim()
    const typeId = Number(ticket?.assignedToTypeId)
    const id = ticket?.assignedToId
    if (id == null || id === '') return '—'
    const typeLabel = getAssigneeTypeLabel(typeId)
    return `${typeLabel} #${id}`
}

export const getInitials = label => {
    const cleaned = String(label || '')
        .replace(/[—–-]/g, ' ')
        .trim()
    if (!cleaned || cleaned === '?') return '?'
    const parts = cleaned.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return '?'
}

export const formatLocation = (name, address) => {
    const n = name?.trim()
    const a = address?.trim()
    if (!n && !a) return '—'
    if (n && a && n.toLowerCase() === a.toLowerCase()) return n
    if (n && a) return `${n} — ${a}`
    return n || a || '—'
}

export const formatTicketDateTime = value => {
    if (!value) return '—'
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value)
        if (!isValid(d)) return String(value)
        return format(d, DateFormat.dateTime)
    } catch {
        return String(value)
    }
}

const parseAnswerFromDescription = description => {
    const text = String(description || '')
    const match = text.match(/Answer:\s*([^\n\r|]+)/i)
    return match?.[1]?.trim() || ''
}

export const formatSlaWindowLabel = (deadlineIso, startIso) => {
    if (!deadlineIso || !startIso) return null
    try {
        const deadline = parseISO(deadlineIso)
        const start = parseISO(startIso)
        if (!isValid(deadline) || !isValid(start)) return null
        const minutes = Math.max(0, differenceInMinutes(deadline, start))
        if (minutes < 60) return `${minutes}m`
        if (minutes < 1440) {
            const h = Math.round(minutes / 60)
            return `${h}h`
        }
        const days = Math.round(minutes / 1440)
        return `${days}d`
    } catch {
        return null
    }
}

export const formatSlaElapsedHint = slaState => {
    if (!slaState?.overdue) return ''
    const raw = String(slaState.countdown || '')
    const match = raw.match(/OVERDUE by\s+(.+)/i)
    if (match?.[1]) return `⚠ elapsed ${match[1]} ago`
    return '⚠ elapsed'
}

export const normalizeTicketDetail = response => {
    const raw = response?.data ?? response?.Data ?? response ?? {}
    const activity = raw.Activity ?? raw.activity ?? []
    const title = stripHtml(raw.Title ?? raw.title ?? '')
    const description = stripHtml(raw.Description ?? raw.description ?? '')
    let questionText = stripHtml(raw.QuestionText ?? raw.questionText ?? '')
    let answerText = stripHtml(raw.AnswerText ?? raw.answerText ?? '')
    if (!questionText) questionText = title
    if (!answerText) {
        answerText = parseAnswerFromDescription(description)
    }
    return {
        ticketId: raw.TicketId ?? raw.ticketId,
        statusId: Number(raw.StatusId ?? raw.statusId ?? 1),
        priorityId: Number(raw.PriorityId ?? raw.priorityId ?? 2),
        ticketType: raw.TicketType ?? raw.ticketType ?? '',
        title,
        questionText,
        answerText,
        ruleId: raw.RuleId ?? raw.ruleId,
        slaAckBy: raw.SLA_AckBy ?? raw.slaAckBy,
        slaResolveBy: raw.SLA_ResolveBy ?? raw.slaResolveBy,
        acknowledgedOn: raw.AcknowledgedOn ?? raw.acknowledgedOn,
        resolvedOn: raw.ResolvedOn ?? raw.resolvedOn,
        closedOn: raw.ClosedOn ?? raw.closedOn,
        createdOn: raw.CreatedOn ?? raw.createdOn,
        auditId: raw.AuditId ?? raw.auditId,
        auditName: raw.AuditName ?? raw.auditName ?? '',
        auditCompletedOn: raw.AuditCompletedOn ?? raw.auditCompletedOn,
        auditSubmittedByName:
            raw.AuditSubmittedByName ?? raw.auditSubmittedByName ?? '',
        locationName: raw.LocationName ?? raw.locationName ?? '',
        locationAddress: raw.LocationAddress ?? raw.locationAddress ?? '',
        assigneeFullName: raw.AssigneeFullName ?? raw.assigneeFullName ?? '',
        assigneeEmail: raw.AssigneeEmail ?? raw.assigneeEmail ?? '',
        assignedToTypeId: raw.AssignedToTypeId ?? raw.assignedToTypeId,
        assignedToId: raw.AssignedToId ?? raw.assignedToId,
        createdByFullName: raw.CreatedByFullName ?? raw.createdByFullName ?? '',
        createdByEmail: raw.CreatedByEmail ?? raw.createdByEmail ?? '',
        activity: (Array.isArray(activity) ? activity : []).map(normalizeActivity),
        recipient: raw.recipient ?? raw.Recipient ?? null,
        escalationLevel: Number(raw.EscalationLevel ?? raw.escalationLevel ?? 0) || 0,
        isOverdue: toBoolFlag(raw.IsOverdue ?? raw.isOverdue),
        lastEscalationOn: raw.LastEscalationOn ?? raw.lastEscalationOn,
        escalationPolicyId:
            raw.EscalationPolicyId ?? raw.escalationPolicyId ?? null,
        escalationPolicyName:
            raw.EscalationPolicyName ?? raw.escalationPolicyName ?? '',
        templateName: raw.TemplateName ?? raw.templateName ?? '',
        allowedActions: normalizeAllowedActions(
            raw.AllowedActions ?? raw.allowedActions,
        ),
        isMyTicket: toBoolFlag(raw.IsMyTicket ?? raw.isMyTicket),
        isAckOverdue: toBoolFlag(raw.IsAckOverdue ?? raw.isAckOverdue),
        isResolveOverdue: toBoolFlag(
            raw.IsResolveOverdue ?? raw.isResolveOverdue,
        ),
    }
}

const ALLOWED_ACTION_ALIASES = {
    comment: 'comment',
    acknowledge: 'ack',
    ack: 'ack',
    resolve: 'resolve',
    close: 'close',
    reassign: 'reassign',
    changeowner: 'reassign',
    'change owner': 'reassign',
    reopen: 'reopen',
    cancel: 'cancel',
    notarealissue: 'cancel',
    'not a real issue': 'cancel',
}

/** null when the API omits AllowedActions so callers can keep status fallbacks. */
export const normalizeAllowedActions = list => {
    if (!Array.isArray(list)) return null
    const actions = new Set()
    list.forEach(item => {
        const key = String(item || '')
            .trim()
            .toLowerCase()
        const mapped = ALLOWED_ACTION_ALIASES[key]
        if (mapped) actions.add(mapped)
    })
    return actions
}

export const normalizeActivity = item => ({
    id: item.TicketActivityLogId ?? item.id,
    actionId: Number(item.ActivityActionId ?? item.activityActionId ?? 0),
    actorTypeId: Number(item.ActorTypeId ?? item.actorTypeId ?? 1),
    actorFullName: item.ActorFullName ?? item.actorFullName ?? '',
    actorEmail: item.ActorEmail ?? item.actorEmail ?? '',
    notes: item.Notes ?? item.notes ?? '',
    createdOn: item.CreatedOn ?? item.createdOn,
})

/** FullName || Email || [external email from Notes] || System */
export const formatActivityActor = item => {
    const full = item?.actorFullName?.trim()
    if (full) return full
    const email = item?.actorEmail?.trim()
    if (email) return email
    if (Number(item?.actorTypeId) === 9 && item?.notes) {
        const match = String(item.notes).match(/^\[([^\]]+)\]/)
        if (match?.[1]) return match[1]
        return 'External'
    }
    if (Number(item?.actionId) === 1) return 'System'
    return 'Staff'
}

/** Strip [email] prefix from external vendor comments for display body */
export const formatActivityNotes = item => {
    const notes = item?.notes ?? ''
    if (Number(item?.actorTypeId) === 9) {
        return String(notes).replace(/^\[[^\]]+\]\s*/, '')
    }
    return notes
}

export const getActivityActionLabel = actionId => {
    switch (Number(actionId)) {
        case 1:
            return 'Ticket created'
        case 2:
            return 'Ticket acknowledged'
        case 4:
            return 'Ticket resolved'
        case 5:
            return 'Ticket reassigned'
        case 6:
            return null
        case 7:
            return 'Ticket closed'
        case 8:
            return 'Reminder sent'
        case 9:
            return 'Escalated'
        case 10:
            return 'SLA breach — marked overdue'
        case 11:
            return 'Reopened'
        case 12:
            return 'Cancelled'
        case 13:
            return 'Released on report approval'
        default:
            return null
    }
}

export const getEscalationHistoryKind = item => {
    const id = Number(item?.actionId)
    // Escalation rail: Reminder / Escalated / Overdue (+ created as baseline)
    if (id === 1) return 'created'
    if (id === 8) return 'reminded'
    if (id === 9) return 'escalated'
    if (id === 10) return 'overdue'
    const notes = String(item?.notes || '')
    if (/^\[Reminded\]/i.test(notes)) return 'reminded'
    if (/^\[Escalated\]/i.test(notes)) return 'escalated'
    if (/^\[Overdue\]/i.test(notes)) return 'overdue'
    return null
}

export const isEscalationHistoryItem = item =>
    Boolean(getEscalationHistoryKind(item))

export const isPureEscalationSweepItem = item => {
    const kind = getEscalationHistoryKind(item)
    return kind === 'reminded' || kind === 'escalated' || kind === 'overdue'
}

export const stripEscalationNotesPrefix = notes =>
    String(notes || '').replace(
        /^\[(Reminded|Escalated|Overdue)\]\s*/i,
        '',
    )

export const getEscalationHistoryTitle = (item, kind) => {
    const fromAction = getActivityActionLabel(item?.actionId)
    if (fromAction) return fromAction
    if (kind === 'reminded') return 'Reminder sent'
    if (kind === 'escalated') return 'Escalated'
    if (kind === 'overdue') return 'SLA breach — marked overdue'
    if (kind === 'created') return 'Ticket created'
    return 'Escalation event'
}

export const filterEscalationHistory = activity =>
    (activity || [])
        .filter(isEscalationHistoryItem)
        .slice()
        .sort((a, b) => {
            const ta = a.createdOn ? new Date(a.createdOn).getTime() : 0
            const tb = b.createdOn ? new Date(b.createdOn).getTime() : 0
            return tb - ta
        })

/** Full ticket History feed — all activity, oldest → newest. */
export const filterHistoryActivity = activity =>
    (activity || []).slice().sort((a, b) => {
        const ta = a.createdOn ? new Date(a.createdOn).getTime() : 0
        const tb = b.createdOn ? new Date(b.createdOn).getTime() : 0
        return ta - tb
    })

/** @deprecated use filterHistoryActivity */
export const filterCommentsActivity = filterHistoryActivity

/**
 * History row: Action (what happened) + Detail (notes / previous response text).
 * Backend often prefixes notes with the same phrase as the action label
 * e.g. "Ticket acknowledged. test" — strip that duplicate prefix.
 */
export const stripDuplicateActionPrefix = (notes, actionLabel) => {
    let detail = String(notes || '').trim()
    const label = String(actionLabel || '').trim()
    if (!detail || !label) return detail
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    detail = detail
        .replace(new RegExp(`^${escaped}\\.?\\s*`, 'i'), '')
        .trim()
    return detail
}

export const formatHistoryEntry = item => {
    const actionId = Number(item?.actionId)
    const rawNotes = formatActivityNotes(item)
    const actionLabel = getActivityActionLabel(actionId)

    if (actionId === 6) {
        return {
            action: 'Comment',
            detail: rawNotes || '',
        }
    }

    if (actionId === 5) {
        const reason = String(rawNotes)
            .replace(/^Reassigned to[^.]*\.\s*/i, '')
            .trim()
        const cleaned =
            reason &&
            reason.toLowerCase() !== String(rawNotes).toLowerCase()
                ? reason
                : ''
        return {
            action: actionLabel || 'Ticket reassigned',
            detail: cleaned,
        }
    }

    const label = actionLabel || 'Update'
    const detail = stripDuplicateActionPrefix(rawNotes, actionLabel)
    return {
        action: label,
        detail,
    }
}

export const extractHistoryMediaUrls = text => {
    const urls = []
    const raw = String(text || '')
    const photoRe = /(?:^|\n)\s*\[(?:Photo|File)\s*\d+\]\s*(https?:\/\/\S+)/gi
    let match = photoRe.exec(raw)
    while (match) {
        urls.push(match[1])
        match = photoRe.exec(raw)
    }
    const bare = raw.match(
        /https?:\/\/\S+\.(?:png|jpe?g|gif|webp)(?:\?\S*)?/gi,
    )
    if (bare) {
        bare.forEach(u => {
            if (!urls.includes(u)) urls.push(u)
        })
    }
    return urls
}

export const stripHistoryMediaUrls = text =>
    String(text || '')
        .replace(/(?:^|\n)\s*\[(?:Photo|File)\s*\d+\]\s*https?:\/\/\S+/gi, '')
        .replace(/https?:\/\/\S+\.(?:png|jpe?g|gif|webp)(?:\?\S*)?/gi, '')
        .trim()

export const normalizeAssignableUsers = response => {
    const list = Array.isArray(response)
        ? response
        : response?.data ??
          response?.Data ??
          response?.Items ??
          response?.Users ??
          []
    return (Array.isArray(list) ? list : [])
        .map(u => {
            const id = Number(u.UniqueId ?? u.unique_id ?? u.Id ?? u.id)
            return {
                id,
                name: formatUserLabel(u),
                email: String(u.Email ?? u.email ?? '').trim(),
                userName: u.UserName ?? u.userName ?? '',
            }
        })
        .filter(u => Number.isFinite(u.id) && u.id > 0)
}

/** Resolve display name for team / vendor assignees from lookup lists. */
export const resolveAssigneeLookupName = (ticket, lookups = {}) => {
    const typeId = Number(ticket?.assignedToTypeId)
    const id = Number(ticket?.assignedToId)
    if (!Number.isFinite(id)) return ''
    if (typeId === 2) {
        const team = (lookups.teams || []).find(t => Number(t.id) === id)
        return team?.name || ''
    }
    if (typeId === 3) {
        const vendor = (lookups.vendors || []).find(v => Number(v.id) === id)
        return vendor?.name || ''
    }
    if (typeId === 4) {
        const contact = (lookups.contacts || []).find(c => Number(c.id) === id)
        return contact?.name || ''
    }
    return ''
}

export const normalizeLookupNameList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || response?.data || []
    return (Array.isArray(list) ? list : [])
        .map(item => ({
            id: Number(
                item.TeamId ??
                    item.GroupId ??
                    item.VendorCompanyId ??
                    item.VendorContactId ??
                    item.Id ??
                    item.id,
            ),
            name: String(
                item.DisplayName ??
                    item.TeamName ??
                    item.GroupName ??
                    item.CompanyName ??
                    item.ContactName ??
                    item.Name ??
                    item.name ??
                    '',
            ).trim(),
        }))
        .filter(item => Number.isFinite(item.id) && item.name)
}

export const getActivityIcon = actionId => {
    switch (Number(actionId)) {
        case 1:
            return '⚙'
        case 2:
            return '✓'
        case 4:
            return '✓✓'
        case 5:
            return '👤'
        case 6:
            return '💬'
        case 7:
            return '🔒'
        case 8:
            return '⏰'
        case 9:
            return '⬆'
        case 10:
            return '⚠'
        case 11:
            return '↺'
        case 12:
            return '✕'
        case 13:
            return '✉'
        default:
            return '•'
    }
}

export const computeSlaState = (deadlineIso, startIso) => {
    if (!deadlineIso) {
        return { percent: 0, tone: 'good', countdown: '—', overdue: false }
    }
    try {
        const deadline = parseISO(deadlineIso)
        const now = new Date()
        if (!isValid(deadline)) {
            return { percent: 0, tone: 'good', countdown: '—', overdue: false }
        }
        const start = startIso ? parseISO(startIso) : now
        const totalMs = deadline - (isValid(start) ? start : now)
        const remainingMs = deadline - now
        if (remainingMs <= 0) {
            const overdueMin = Math.abs(differenceInMinutes(now, deadline))
            return {
                percent: 100,
                tone: 'danger',
                countdown: formatOverdueLabel(overdueMin),
                overdue: true,
            }
        }
        const percent =
            totalMs > 0
                ? Math.max(0, Math.min(100, (1 - remainingMs / totalMs) * 100))
                : 0
        const elapsedPercent = 100 - percent
        let tone = 'good'
        if (elapsedPercent >= 80) tone = 'danger'
        else if (elapsedPercent >= 50) tone = 'warn'
        return {
            percent: elapsedPercent,
            tone,
            countdown: formatCountdownMinutes(differenceInMinutes(deadline, now)),
            overdue: false,
        }
    } catch {
        return { percent: 0, tone: 'good', countdown: '—', overdue: false }
    }
}

const formatCountdownMinutes = minutes => {
    const m = Math.max(0, Number(minutes) || 0)
    if (m < 60) return `${m} min`
    if (m < 1440) {
        const h = Math.floor(m / 60)
        const rem = m % 60
        return rem ? `${h} h ${rem} m` : `${h} h`
    }
    const days = Math.floor(m / 1440)
    const remH = Math.floor((m % 1440) / 60)
    return remH ? `${days} day${days === 1 ? '' : 's'} ${remH} h` : `${days} day${days === 1 ? '' : 's'}`
}

const formatOverdueLabel = minutes => {
    const m = Math.max(1, Number(minutes) || 0)
    if (m < 60) return `OVERDUE by ${m} min`
    const h = Math.floor(m / 60)
    const rem = m % 60
    return rem ? `OVERDUE by ${h}h ${rem}m` : `OVERDUE by ${h}h`
}

export const isAnswerNegative = text => {
    const t = String(text || '').toLowerCase()
    return (
        t.includes('no') ||
        t.includes('fail') ||
        t.includes('non') ||
        t.includes('not') ||
        t.includes('cump')
    )
}

export const getApiErrorMessage = err =>
    err?.error?.message ||
    err?.error?.Message ||
    err?.message ||
    'Something went wrong. Please try again.'

/** Numeric template id for GET /Template/{id}/Tickets from an inspection audit payload. */
export const resolveTemplateNumericIdFromAudit = audit => {
    if (!audit) return null
    const candidate =
        audit.OriginalTemplateId ??
        audit.originalTemplateId ??
        audit.TemplateId ??
        audit.templateId
    const n = Number(candidate)
    return Number.isFinite(n) && n > 0 ? n : null
}

export const normalizeTemplateTicketList = response => {
    const raw = response?.data ?? response?.Data ?? response ?? []
    const list = Array.isArray(raw) ? raw : []
    return list.map(t => ({
        ticketId: t.TicketId ?? t.ticketId,
        auditId: t.AuditId ?? t.auditId,
        questionId: t.QuestionId ?? t.questionId,
        ruleId: t.RuleId ?? t.ruleId,
        statusId: Number(t.StatusId ?? t.statusId ?? 1),
        priorityId: Number(t.PriorityId ?? t.priorityId ?? 2),
        assignedToTypeId: t.AssignedToTypeId ?? t.assignedToTypeId,
        assignedToId: t.AssignedToId ?? t.assignedToId,
        title: stripHtml(t.Title ?? t.title ?? ''),
        createdOn: t.CreatedOn ?? t.createdOn,
        updatedOn: t.UpdatedOn ?? t.updatedOn,
        escalationLevel: Number(t.EscalationLevel ?? t.escalationLevel ?? 0) || 0,
        isOverdue: toBoolFlag(t.IsOverdue ?? t.isOverdue),
        lastEscalationOn: t.LastEscalationOn ?? t.lastEscalationOn,
        escalationPolicyId: t.EscalationPolicyId ?? t.escalationPolicyId ?? null,
        escalationPolicyName:
            t.EscalationPolicyName ?? t.escalationPolicyName ?? '',
    }))
}

export const computeEscalationStats = (tickets = []) => {
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
    const recent = tickets.filter(t => {
        if (!t.createdOn) return false
        const ts = new Date(t.createdOn).getTime()
        return Number.isFinite(ts) && ts > thirtyDaysAgo
    })
    let escalated = 0
    let overdue = 0
    recent.forEach(t => {
        if (
            Number(t.statusId) === TICKET_STATUS_ESCALATED ||
            Number(t.escalationLevel) >= 2
        ) {
            escalated += 1
        }
        if (toBoolFlag(t.isOverdue)) overdue += 1
    })
    return { escalated, overdue }
}

export const ticketMatchesEscalationFilter = (ticket, filter) => {
    if (!filter) return true
    if (filter === 'escalated') {
        return (
            Number(ticket.statusId) === TICKET_STATUS_ESCALATED ||
            Number(ticket.escalationLevel) >= 2
        )
    }
    if (filter === 'overdue') return toBoolFlag(ticket.isOverdue)
    if (filter === 'reminded') {
        return (
            Number(ticket.escalationLevel) >= 1 &&
            Number(ticket.statusId) !== TICKET_STATUS_ESCALATED
        )
    }
    return true
}

export const ticketMatchesSearch = (ticket, query) => {
    const q = String(query || '')
        .trim()
        .toLowerCase()
    if (!q) return true
    const id = String(ticket.ticketId ?? '')
    const title = String(ticket.title ?? '').toLowerCase()
    const rule = String(ticket.ruleId ?? '')
    const question = String(ticket.questionText ?? '').toLowerCase()
    const answer = String(ticket.answerText ?? '').toLowerCase()
    return (
        id.includes(q) ||
        title.includes(q) ||
        rule.includes(q) ||
        question.includes(q) ||
        answer.includes(q)
    )
}

export const getPriorityDisplayLabel = priorityId => {
    const meta = TICKET_PRIORITY[priorityId] || TICKET_PRIORITY[2]
    if (meta.pill === 'critical') return 'Urgent'
    return meta.label.charAt(0) + meta.label.slice(1).toLowerCase()
}

export const getStatusDisplayLabel = statusId => {
    const meta = TICKET_STATUS[statusId] || TICKET_STATUS[1]
    const label = meta.label.toLowerCase()
    if (label === 'in progress') return 'In Progress'
    return label.charAt(0).toUpperCase() + label.slice(1)
}

export const formatTicketRelativeTime = value => {
    if (!value) return ''
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value)
        if (!isValid(d)) return ''
        return formatDistanceToNow(d, { addSuffix: true })
    } catch {
        return ''
    }
}

export const getTicketStatusWhenLabel = ticket => {
    const statusId = Number(ticket?.statusId ?? 1)
    if (statusId === 4) {
        const t = formatTicketRelativeTime(ticket.updatedOn || ticket.createdOn)
        return t ? `Resolved ${t.replace(/^about /, '')}` : ''
    }
    if (statusId === 5) {
        const t = formatTicketRelativeTime(ticket.updatedOn || ticket.createdOn)
        return t ? `Closed ${t.replace(/^about /, '')}` : ''
    }
    if (statusId === 3) {
        const t = formatTicketRelativeTime(ticket.updatedOn || ticket.createdOn)
        return t ? `Updated ${t.replace(/^about /, '')}` : ''
    }
    const t = formatTicketRelativeTime(ticket.createdOn)
    return t ? `Created ${t.replace(/^about /, '')}` : ''
}

export const computeTicketStatusCounts = tickets => {
    const counts = {
        total: tickets.length,
        open: 0,
        inProgress: 0,
        resolved: 0,
        closed: 0,
        pending: 0,
        cancelled: 0,
    }
    tickets.forEach(t => {
        const id = Number(t.statusId ?? 1)
        if (id === 3) counts.inProgress += 1
        else if (id === 4) counts.resolved += 1
        else if (id === 5 || id === 10) counts.closed += 1
        else if (id === TICKET_STATUS_PENDING_APPROVAL) counts.pending += 1
        else if (isTicketCancelledStatus(id)) counts.cancelled += 1
        else counts.open += 1
    })
    return counts
}

export const countApprovalReleaseTickets = (tickets = []) => {
    let pending = 0
    let cancelled = 0
    tickets.forEach(ticket => {
        const id = Number(ticket?.statusId)
        if (id === TICKET_STATUS_PENDING_APPROVAL) pending += 1
        else if (id === TICKET_STATUS_CANCELLED) cancelled += 1
    })
    return { pending, cancelled }
}

export const normalizeCompanyTicketSetting = response => {
    const raw = response?.Setting ?? response?.setting ?? response ?? {}
    const setting = raw?.NotificationMode != null || raw?.notificationMode != null
        ? raw
        : raw?.Setting ?? raw?.setting ?? raw
    return {
        notificationMode: Number(
            setting?.NotificationMode ?? setting?.notificationMode ?? 1,
        ),
        releaseOn: Number(setting?.ReleaseOn ?? setting?.releaseOn ?? 2),
    }
}

export const normalizeTicketListResponse = response => {
    const raw = response?.data ?? response?.Data ?? response ?? {}
    const list = Array.isArray(raw)
        ? raw
        : raw.Items ??
          raw.items ??
          raw.Tickets ??
          raw.tickets ??
          raw.Data ??
          raw.data ??
          []
    const items = (Array.isArray(list) ? list : []).map(ticket => ({
        ticketId: ticket.TicketId ?? ticket.ticketId ?? ticket.Id ?? ticket.id,
        title: stripHtml(ticket.Title ?? ticket.title ?? ''),
        templateName:
            ticket.TemplateName ??
            ticket.templateName ??
            ticket.AuditName ??
            ticket.auditName ??
            '',
        // 2026-10-22 "By inspection" view
        auditId: Number(ticket.AuditId ?? ticket.auditId ?? 0) || 0,
        auditName: ticket.AuditName ?? ticket.auditName ?? '',
        assignedToTypeId: Number(
            ticket.AssignedToTypeId ?? ticket.assignedToTypeId ?? 0,
        ),
        assignedToId: ticket.AssignedToId ?? ticket.assignedToId ?? null,
        statusId: Number(ticket.StatusId ?? ticket.statusId ?? 1),
        priorityId: Number(ticket.PriorityId ?? ticket.priorityId ?? 2),
        ruleId: ticket.RuleId ?? ticket.ruleId,
        questionText: stripHtml(
            ticket.QuestionText ?? ticket.questionText ?? '',
        ),
        answerText: stripHtml(ticket.AnswerText ?? ticket.answerText ?? ''),
        escalationLevel:
            Number(ticket.EscalationLevel ?? ticket.escalationLevel ?? 0) || 0,
        isOverdue: toBoolFlag(ticket.IsOverdue ?? ticket.isOverdue),
        lastEscalationOn:
            ticket.LastEscalationOn ?? ticket.lastEscalationOn ?? null,
        createdOn:
            ticket.CreatedOn ?? ticket.createdOn ?? ticket.CreatedDate ?? null,
        updatedOn: ticket.UpdatedOn ?? ticket.updatedOn ?? null,
    }))
    const total = Number(
        raw.TotalCount ??
            raw.totalCount ??
            raw.Total ??
            response?.TotalCount ??
            items.length,
    )
    return {
        items,
        totalCount: Number.isFinite(total) ? total : items.length,
    }
}

export const buildQuestionAnswerMapFromAudit = audit => {
    const map = new Map()
    const sections = audit?.Sections ?? audit?.sections ?? []
    sections.forEach(section => {
        const questions = section?.Questions ?? section?.questions ?? []
        questions.forEach(q => {
            const qid = Number(q.QuestionId ?? q.questionId)
            if (!qid) return
            const questionText = stripHtml(
                q.FAQ ?? q.QuestionText ?? q.questionText ?? q.Title ?? '',
            )
            let answerText = ''
            const multi =
                q.Answers?.MultipleResponse ??
                q.answers?.multipleResponse ??
                []
            const selected = multi.find(
                r => r.IsSelected || r.isSelected,
            )
            if (selected) {
                answerText =
                    selected.DisplayText ?? selected.displayText ?? ''
            } else if (q.Answers?.SingleResponse) {
                answerText =
                    q.Answers.SingleResponse.DisplayText ??
                    q.Answers.SingleResponse.displayText ??
                    ''
            }
            map.set(qid, { questionText, answerText })
        })
    })
    return map
}

export const enrichAuditTickets = (tickets, audit, qaMap) => {
    const map = qaMap || buildQuestionAnswerMapFromAudit(audit)
    return tickets.map(t => {
        const qa = map.get(Number(t.questionId)) || {}
        return {
            ...t,
            questionText: stripHtml(
                t.questionText || qa.questionText || t.title || '',
            ),
            answerText: stripHtml(t.answerText || qa.answerText || ''),
            assigneeFullName: t.assigneeFullName || t.assigneeName || '',
            assigneeEmail: t.assigneeEmail || '',
        }
    })
}

export const normalizeDigestResponse = response =>
    normalizeAuditTicketsByAudit(response)

export const normalizeAuditTicketsByAudit = response => {
    const raw = response?.data ?? response?.Data ?? response ?? {}
    const ticketsRaw = raw.Tickets ?? raw.tickets ?? raw.data ?? []
    const list = Array.isArray(ticketsRaw) ? ticketsRaw : []
    const tickets = list.map(t => ({
        ticketId: t.TicketId ?? t.ticketId,
        auditId: t.AuditId ?? t.auditId,
        questionId: t.QuestionId ?? t.questionId,
        ruleId: t.RuleId ?? t.ruleId,
        statusId: Number(t.StatusId ?? t.statusId ?? 1),
        priorityId: Number(t.PriorityId ?? t.priorityId ?? 2),
        assignedToTypeId: t.AssignedToTypeId ?? t.assignedToTypeId,
        assignedToId: t.AssignedToId ?? t.assignedToId,
        title: stripHtml(t.Title ?? t.title ?? ''),
        questionText: stripHtml(
            t.QuestionText ?? t.questionText ?? t.Title ?? '',
        ),
        answerText: stripHtml(t.AnswerText ?? t.answerText ?? ''),
        assigneeFullName:
            t.AssigneeFullName ??
            t.assigneeFullName ??
            t.AssignedToName ??
            '',
        assigneeEmail: t.AssigneeEmail ?? t.assigneeEmail ?? '',
        createdOn: t.CreatedOn ?? t.createdOn,
        updatedOn: t.UpdatedOn ?? t.updatedOn,
        escalationLevel: Number(t.EscalationLevel ?? t.escalationLevel ?? 0) || 0,
        isOverdue: toBoolFlag(t.IsOverdue ?? t.isOverdue),
        lastEscalationOn: t.LastEscalationOn ?? t.lastEscalationOn,
        escalationPolicyId: t.EscalationPolicyId ?? t.escalationPolicyId ?? null,
        escalationPolicyName:
            t.EscalationPolicyName ?? t.escalationPolicyName ?? '',
    }))
    const countsRaw = raw.Counts ?? raw.counts ?? {}
    return {
        auditId: raw.AuditId ?? raw.auditId,
        auditName: raw.AuditName ?? raw.auditName ?? '',
        templateName: raw.TemplateName ?? raw.templateName ?? '',
        completedByName:
            raw.CompletedByName ??
            raw.completedByName ??
            raw.CreatedByName ??
            '',
        completedByEmail:
            raw.CompletedByEmail ??
            raw.completedByEmail ??
            raw.CreatedByEmail ??
            '',
        completedOn:
            raw.CompletedOn ??
            raw.completedOn ??
            raw.AuditCompletedOn,
        counts: {
            total: Number(countsRaw.Total ?? countsRaw.total ?? tickets.length),
            open: Number(countsRaw.Open ?? countsRaw.open ?? 0),
            inProgress: Number(
                countsRaw.InProgress ?? countsRaw.inProgress ?? 0,
            ),
            resolved: Number(countsRaw.Resolved ?? countsRaw.resolved ?? 0),
            closed: Number(countsRaw.Closed ?? countsRaw.closed ?? 0),
        },
        tickets,
    }
}

export const ticketMatchesAuditListFilters = (
    ticket,
    { statusId, priorityId, assigneeKey, search },
) => {
    if (statusId && Number(ticket.statusId) !== Number(statusId)) {
        return false
    }
    if (priorityId && Number(ticket.priorityId) !== Number(priorityId)) {
        return false
    }
    if (assigneeKey) {
        const key = `${ticket.assigneeFullName || ''}|${ticket.assigneeEmail || ''}`
        if (key !== assigneeKey) return false
    }
    return ticketMatchesSearch(ticket, search)
}
